import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, EMPTY } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { ChartConfiguration, ChartOptions } from 'chart.js';

import { DashboardWidgetsModule } from 'app/inter-chain/dashboard/widgets/dashboard-widgets.module';
import { CasesPropertiesService } from 'app/inter-chain/cases/services/cases-properties.service';
import { PerformanceCase } from 'app/inter-chain/dashboard/models/PerfeormanceCase.model';
import { PerformanceTrendsData } from 'app/inter-chain/cases/models/PerformanceTrendsData.model';
import { DashboardWidgetCardComponent } from '../dashboard-widget-card/dashboard-widget-card.component';
import {
  buildChartDataset,
  buildPercentChartOptions,
  emptyChartData,
  fillTimeSeriesGaps,
  DECREASE_COLOR,
  DECREASE_BG_COLOR,
  INCREASE_COLOR,
  INCREASE_BG_COLOR,
} from '../../utils/chart-config.util';
import { getCasesQualificationKey } from '../../utils/qualifications.util';

@Component({
  selector: 'mh-resolved-cases-widget-container',
  templateUrl: './resolved-cases-widget-container.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DashboardWidgetsModule, DashboardWidgetCardComponent, TranslateModule],
})
export class ResolvedCasesWidgetContainerComponent {
  customerId = input.required<number>();
  enabled = input(true);

  rawData = signal<PerformanceCase | null>(null);
  displayValue = signal<string | null>(null);
  loading = signal(true);
  error = signal(false);
  ready = computed(() => !this.loading());
  chartData = signal<ChartConfiguration['data']>(emptyChartData());
  chartOptions = signal<ChartOptions>({});

  changePercentage = computed(() => this.rawData()?.average?.resolved?.rate?.variation ?? null);
  isIncrease = computed(() => (this.rawData()?.average?.resolved?.rate?.variation ?? 0) > 0);

  private destroyRef = inject(DestroyRef);
  private casesService = inject(CasesPropertiesService);
  private translateService = inject(TranslateService);

  constructor() {
    combineLatest([toObservable(this.customerId), toObservable(this.enabled)])
      .pipe(
        switchMap(([, enabled]) => {
          this.reset();
          if (!enabled) {
            this.setDisabled();
            return EMPTY;
          }
          return this.fetchData();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private reset(): void {
    this.rawData.set(null);
    this.displayValue.set(null);
    this.loading.set(true);
    this.error.set(false);
    this.chartData.set(emptyChartData());
    this.chartOptions.set({});
  }

  private setDisabled(): void {
    this.loading.set(false);
  }

  private fetchData() {
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    const monthAgo = DateTime.now().minus({ days: 30 }).toFormat('yyyy-MM-dd');
    const dates = { startDate: monthAgo, endDate: today };

    return this.casesService.getPerformanceObs([this.customerId()], dates).pipe(
      tap((response: PerformanceCase) => {
        if (!response) return;
        const resolvedActual = response.average?.resolved?.rate?.actual;
        response.description = this.translateService.instant(getCasesQualificationKey(resolvedActual));
        this.rawData.set(response);
        this.displayValue.set(resolvedActual != null ? `${resolvedActual.toFixed()}%` : null);
      }),
      switchMap((response: PerformanceCase) => {
        if (!response) return EMPTY;
        return this.casesService.getPerformanceTrends(`${this.customerId()}`, dates).pipe(
          tap((trendsResponse: PerformanceTrendsData[]) => {
            if (!trendsResponse) return;
            const rawPeriods = trendsResponse.map((item) => ({ period: item.period, value: item.data.resolved ?? null }));
            const filled = fillTimeSeriesGaps(rawPeriods);
            const resolvedValues = filled.map((p) => p.value as number);
            const nonNullValues = filled.map((p) => p.value).filter((v): v is number => v !== null);
            const increase = this.isIncrease();
            this.chartOptions.set(buildPercentChartOptions(nonNullValues.length ? Math.max(...nonNullValues) : 100));
            this.chartData.set(
              buildChartDataset(
                filled.map((p) => p.period),
                resolvedValues,
                increase ? INCREASE_COLOR : DECREASE_COLOR,
                increase ? INCREASE_BG_COLOR : DECREASE_BG_COLOR,
              ),
            );
          }),
          catchError(() => EMPTY),
        );
      }),
      catchError(() => {
        this.error.set(true);
        return EMPTY;
      }),
      finalize(() => this.loading.set(false)),
    );
  }
}
